// onboarding-flows.ts — the Bridge home screen's first-visit flow (WORK
// ITEM 63 PR 2). FLOWS ARE DATA over the shared pi-stack engine; this file
// owns the console's surfaces (home — users/audit are admin pages and
// deliberately carry no tour). Seen-state rides the console's OWN ui_state
// seat (/api/ui-state/onboarding/global, migration 062) — the same per-user
// map the Helm client reads through the /__console carve-out, so finishing
// the Bridge tour here never replays it inside a release, and vice versa.
//
// TARGET IDS ARE A CONTRACT: every target below must exist as a literal
// id="..." in this client's source — onboarding-flows.test.mjs drills it, so
// a renamed id breaks CI, not the tour. Targets legitimately absent at
// runtime (hcCreate for a user without the capability, an empty hcRecents)
// auto-skip, visibly logged — the engine's contract.

import type { FlowDef } from './onboarding.js';
import {
  createOnboardingStore,
  installFirstVisitFlows,
  registerFlows,
  flowInventory,
} from 'pi-stack/client/onboarding-flows.js';
import { installWindowApi } from 'pi-stack/client/onboarding-api.js';

export const CONSOLE_FLOWS: FlowDef[] = [
  {
    id: 'bridge-home',
    surface: 'home',
    steps: [
      {
        targets: [],
        title: 'Welcome to the Bridge',
        body: 'This is the Bridge — the control plane. Each release below is a full agent fleet building software; open one and you are on its Helm, watching the work happen live.',
      },
      {
        targets: [{ id: 'hcGrid', padding: 4 }],
        title: 'Your releases',
        body: 'Every release you can reach, with its status, activity and spend. Click a row to step aboard.',
        placement: 'above',
      },
      {
        targets: [{ id: 'hcCreate' }],
        title: 'Start a release',
        body: 'Create a new release here: name it, point it at a repository, and the fleet takes it from ideation onward.',
      },
      {
        targets: [{ id: 'hcRecents', padding: 4 }],
        title: 'Pick up where you left off',
        body: 'The releases you visited most recently — one click back to the deck you were on.',
      },
      {
        // Owner ruling 2026-09-08: ONE control. The appearance picker moved
        // INSIDE the avatar menu (and the gear beside it retired), so the
        // spotlight names the circle alone.
        targets: [{ id: 'hcAccountBtn' }],
        title: 'You and your view',
        body: 'Your account lives here — and inside it the appearance picker: dark or light, the theme, the style. The look you pick follows you into every release.',
        placement: 'below',
      },
    ],
  },
  {
    // Item 63 follow-up (owner): "we also need an onboarding flow for the
    // templates page in the bridge." The page is TWO surfaces (the list at
    // #/config-templates, the editor at #/config-templates/<id>), so it gets
    // TWO flows — a single flow would auto-skip half its steps on whichever
    // page the user landed on first. COORDINATION (lane h is reshaping the
    // toolbar): targets are the STABLE anchors only — grid, section nav,
    // editor body; search/clone/disable/save live in the COPY, never as
    // spotlight targets, so the toolbar rework cannot break this flow or
    // trip the drift guard.
    id: 'bridge-templates',
    surface: 'config-templates',
    steps: [
      {
        targets: [],
        title: 'Configuration templates',
        body: 'Every variable the Helm can be configured with lives here, gathered into templates. When you create a release you pick one, and its values become that release\u2019s configuration.',
      },
      {
        targets: [{ id: 'hcCtGrid', padding: 4 }],
        title: 'Your templates',
        body: 'All templates, live. One is always the default \u2014 the one release creation preselects \u2014 so there is always a working configuration to start from. Open a row to edit it.',
        placement: 'above',
      },
      {
        targets: [],
        title: 'Clone and disable',
        body: 'Start a new template by cloning an existing one, and disable templates you no longer offer \u2014 disabled rows keep their history but leave the release-creation picker.',
      },
    ],
  },
  {
    id: 'bridge-templates-editor',
    surface: 'config-templates-editor',
    steps: [
      {
        targets: [],
        title: 'The template editor',
        body: 'This is one template, every setting it carries. Nothing saves until you say so \u2014 explore freely.',
      },
      {
        targets: [{ id: 'cfgNav', padding: 4 }],
        title: 'Find any setting',
        body: 'Settings are grouped into sections \u2014 click to jump, or use search to cut straight to one by name.',
        placement: 'right',
      },
      {
        targets: [{ id: 'cfgContent', padding: 4 }],
        title: 'Edit with a safety net',
        body: 'Values changed from the default are marked as you edit. Saving opens a diff review first \u2014 you approve exactly what changes before it lands.',
        placement: 'left',
      },
    ],
  },
];

/**
 * Boot the console's onboarding: register the flows, create the store on the
 * console's own seat, and return the route hook app.ts calls after each
 * mount ('home' | 'users' | 'audit' — only 'home' has a flow today).
 */
export function bootOnboarding(): (surface: string) => void {
  registerFlows(CONSOLE_FLOWS);
  // Item 63 PR 3: the programmatic seam (window.helmOnboarding) — same
  // contract as the Helm client installs; see docs/onboarding-overlays.md.
  installWindowApi();
  const store = createOnboardingStore({
    seatUrl: '/api/ui-state/onboarding/global',
    localKey: 'hc-onboarding',
  });
  const inv = flowInventory();
  console.info(`[onboarding] console flows registered: ${inv.flows} flows, ${inv.steps} steps, ${inv.surfaces} surfaces`);
  return installFirstVisitFlows(store);
}
